import { Trans, useLingui } from '@lingui/react/macro';
import { Box, Link, TextField, Typography } from '@mui/material';
import type { ChangeEvent } from 'react';
import useAuthStore from '../../../store/rootStore.ts';

interface InitialStepProps {
  accessCode: string;
  onAccessCodeChange: (value: string) => void;
  error: string;
  isLoading: boolean;
  onErrorClear: () => void;
  displayAddress: string;
}

const textFieldSx = {
  '& .MuiInputBase-root': {
    height: '40px',
  },
};

const textFieldErrorSx = {
  '& .MuiInputBase-root': {
    height: '40px',
  },
  '& .MuiOutlinedInput-root::before': {
    background: 'var(--mui-palette-error-main)',
  },
};

export const InitialStep = ({
  accessCode,
  onAccessCodeChange,
  error,
  isLoading,
  onErrorClear,
  displayAddress,
}: InitialStepProps) => {
  const { t } = useLingui();
  const discordUrl = useAuthStore((s) => s.config?.features.discordUrl);
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onAccessCodeChange(event.target.value);
    if (error) {
      onErrorClear();
    }
  };

  return (
    <>
      {!error && (
        <Box sx={{ mb: 3 }}>
          <Typography>
            <Trans>
              Perpl is currently in a gated beta phase.
              <br />
              You can request access codes in our{' '}
              <Link href={discordUrl} target="_blank" rel="noopener noreferrer">
                Discord
              </Link>
              .
            </Trans>
          </Typography>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <Trans>Access Code</Trans>
      </Typography>
      <TextField
        fullWidth
        value={accessCode}
        onChange={handleChange}
        placeholder={t`Input access code`}
        disabled={isLoading}
        error={!!error}
        autoFocus
        sx={error ? textFieldErrorSx : textFieldSx}
      />

      {!!error && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {!error && displayAddress && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            <Trans>Address</Trans>
          </Typography>
          <TextField fullWidth value={displayAddress} disabled sx={textFieldSx} />
        </>
      )}
    </>
  );
};

export const RestrictedStep = () => {
  const waitlistUrl = useAuthStore((s) => s.config?.features.waitlistUrl);
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body1" color="text.secondary">
        <Trans>
          We're currently in private beta and onboarding a limited number of users. Your wallet isn't on the list just yet, but
          we're opening more spots soon.{' '}
          <Link href={waitlistUrl} target="_blank" rel="noopener" color="primary">
            Join the waitlist to get access.
          </Link>
        </Trans>
      </Typography>
    </Box>
  );
};
